import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { i18next } from '@biofarma/i18n';
import { pwaApi } from '../lib/api.js';

// The language choice lives in localStorage, not in the sessionStorage-persisted store:
// sessionStorage is cleared when the tablet closes the app, which used to reset the language
// on every restart and made /tablet/context load machine states in Italian.
export const LANG_STORAGE_KEY = 'biofarma_pwa_lang';

function readStoredLanguage(): string | null {
  try {
    return localStorage.getItem(LANG_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredLanguage(code: string): void {
  try {
    localStorage.setItem(LANG_STORAGE_KEY, code);
  } catch {
    // storage unavailable (private mode): the language stays in memory for this run only
  }
}

interface MachineStateCategory {
  id: number;
  valueIt: string;
  valueLocale: string;
}

interface MachineStateSubcategory {
  id: number;
  valueIt: string;
  valueLocale: string;
  category: MachineStateCategory;
}

interface MachineState {
  id: number;
  valueIt: string;
  valueLocale: string;
  color: string;
  sortOrder: number;
  isDefault: boolean;
  subcategory: MachineStateSubcategory;
}

interface StateLog {
  id: number;
  sessionId: number;
  machineStateId: number;
  note: string | null;
  occurredAt: string;
  machineState: { id: number; valueIt: string; valueLocale: string; color: string };
}

interface Device {
  id: number;
  code: string;
  name: string;
  deviceType: { id: number; valueIt: string } | null;
  parameters: { id: number; key: string; labelIt: string; unit: { symbol: string } | null }[];
}

interface ProductionSummary {
  producedTotal: number;
  rejectTotal: number;
}

export interface OperatorLog {
  id: number;
  sessionId: number;
  operatorNumber: number;
  loginAt: string;
  logoutAt: string | null;
}

export const OPERATOR_NUMBERS = [1, 2, 3, 4, 5, 6, 7, 8] as const;

interface SessionState {
  // Contesto tablet
  machineId: number | null;
  machineName: string | null;
  machineCode: string | null;
  plantGroupId: number | null;
  plantGroupName: string | null;
  plantName: string | null;
  plantCountry: string | null;
  plantTimezone: string;
  defaultLanguageCode: string;
  availableStates: MachineState[];
  devices: Device[];

  // Sessione corrente
  sessionId: number | null;
  isSessionActive: boolean;
  currentOrderCode: string | null;
  /** udm dell'ordine in corso, così come scritta dall'ERP (può essere vuota su ordini vecchi) */
  currentOrderUdm: string | null;
  currentOrderQuantity: string | null;
  /** login/logout degli operatori sulla sessione corrente: più operatori insieme */
  operators: OperatorLog[];
  currentStateLog: StateLog | null;
  productionSummary: ProductionSummary;

  // Lingua
  language: string | null;

  // Status
  isOnline: boolean;
  isLoadingContext: boolean;
  contextError: string | null;

  // Actions
  loadContext(): Promise<void>;
  refreshOperators(): Promise<void>;
  loginOperator(operatorNumber: number): Promise<void>;
  logoutOperator(operatorNumber: number): Promise<void>;
  setLanguage(code: string): void;
  startSession(order: { id: number; workOrder: string; udm: string; productionQuantity: string }): Promise<void>;
  endSession(): Promise<void>;
  addStateLog(machineStateId: number, note?: string): Promise<void>;
  refreshProductionSummary(): Promise<void>;
  setOnline(online: boolean): void;
}

export const useSessionStore = create<SessionState>()(
  persist(
    (set, get) => ({
      machineId: null,
      machineName: null,
      machineCode: null,
      plantGroupId: null,
      plantGroupName: null,
      plantName: null,
      plantCountry: null,
      plantTimezone: 'Europe/Rome',
      defaultLanguageCode: 'it',
      availableStates: [],
      devices: [],
      sessionId: null,
      isSessionActive: false,
      currentOrderCode: null,
      currentOrderUdm: null,
      currentOrderQuantity: null,
      operators: [],
      currentStateLog: null,
      productionSummary: { producedTotal: 0, rejectTotal: 0 },
      language: readStoredLanguage(),
      isOnline: navigator.onLine,
      isLoadingContext: false,
      contextError: null,

      async loadContext() {
        set({ isLoadingContext: true, contextError: null });
        try {
          const requestedLang = get().language;
          const { data } = await pwaApi.get('/tablet/context', { params: requestedLang ? { lang: requestedLang } : undefined });
          const ctx = data.data as {
            machine: { id: number; code: string; name: string };
            plantGroup: { id: number; name: string; plant: { id: number; name: string; country: string; timezone: string; defaultLanguageCode: string } };
            availableStates: MachineState[];
            devices: Device[];
            currentSession: {
              id: number;
              startedAt: string;
              productionOrder: { id: number; workOrder: string; udm: string; productionQuantity: string; sku: string } | null;
            } | null;
            currentStateLog: StateLog | null;
            operators: OperatorLog[];
          };
          const resolvedLang = requestedLang ?? ctx.plantGroup.plant.defaultLanguageCode;
          set({
            machineId: ctx.machine.id,
            machineName: ctx.machine.name,
            machineCode: ctx.machine.code,
            plantGroupId: ctx.plantGroup.id,
            plantGroupName: ctx.plantGroup.name,
            plantName: ctx.plantGroup.plant.name,
            plantCountry: ctx.plantGroup.plant.country,
            plantTimezone: ctx.plantGroup.plant.timezone,
            defaultLanguageCode: ctx.plantGroup.plant.defaultLanguageCode,
            availableStates: ctx.availableStates,
            devices: ctx.devices,
            sessionId: ctx.currentSession?.id ?? null,
            isSessionActive: ctx.currentSession !== null,
            currentOrderCode: ctx.currentSession?.productionOrder?.workOrder ?? get().currentOrderCode,
            currentOrderUdm: ctx.currentSession?.productionOrder?.udm ?? get().currentOrderUdm,
            currentOrderQuantity: ctx.currentSession?.productionOrder?.productionQuantity ?? get().currentOrderQuantity,
            currentStateLog: ctx.currentStateLog,
            operators: ctx.operators ?? [],
            language: resolvedLang,
            isLoadingContext: false,
          });
          void i18next.changeLanguage(resolvedLang);

          if (requestedLang === null) {
            writeStoredLanguage(resolvedLang);
            // First run after an app restart: the context above was fetched with no ?lang= and
            // therefore holds the Italian master values. Reload it once now that the plant
            // default language is known, so machine states match the interface language.
            if (resolvedLang !== 'it') {
              await get().loadContext();
              return;
            }
          }

          if (ctx.currentSession) await get().refreshProductionSummary();
        } catch (e) {
          set({ isLoadingContext: false, contextError: String(e) });
        }
      },

      async refreshOperators() {
        const { sessionId } = get();
        if (!sessionId) return set({ operators: [] });
        const { data } = await pwaApi.get(`/tablet/sessions/${sessionId}/operators`);
        set({ operators: data.data as OperatorLog[] });
      },

      async loginOperator(operatorNumber) {
        const { sessionId } = get();
        if (!sessionId) return;
        await pwaApi.post(`/tablet/sessions/${sessionId}/operators`, { operatorNumber });
        await get().refreshOperators();
      },

      async logoutOperator(operatorNumber) {
        const { sessionId } = get();
        if (!sessionId) return;
        await pwaApi.put(`/tablet/sessions/${sessionId}/operators/${operatorNumber}/logout`);
        await get().refreshOperators();
      },

      setLanguage(code) {
        writeStoredLanguage(code);
        set({ language: code });
        void i18next.changeLanguage(code);
        void get().loadContext();
      },

      async startSession(order) {
        const { data } = await pwaApi.post('/tablet/sessions', { productionOrderId: order.id, notes: `Ordine: ${order.workOrder}` });
        const session = data.data as { id: number };
        set({
          sessionId: session.id,
          isSessionActive: true,
          currentOrderCode: order.workOrder,
          currentOrderUdm: order.udm,
          currentOrderQuantity: order.productionQuantity,
          operators: [],
          productionSummary: { producedTotal: 0, rejectTotal: 0 },
        });
      },

      async endSession() {
        const { sessionId } = get();
        if (!sessionId) return;
        await pwaApi.put(`/tablet/sessions/${sessionId}/end`);
        set({
          sessionId: null,
          isSessionActive: false,
          currentOrderCode: null,
          currentOrderUdm: null,
          currentOrderQuantity: null,
          currentStateLog: null,
          operators: [],
          productionSummary: { producedTotal: 0, rejectTotal: 0 },
        });
      },

      async addStateLog(machineStateId, note) {
        const { sessionId } = get();
        if (!sessionId) return;
        const { data } = await pwaApi.post(`/tablet/sessions/${sessionId}/state-logs`, { machineStateId, note });
        const created = data.data as StateLog;
        // La riga creata torna con i soli valori italiani (è il master dell'anagrafica): la
        // etichetta tradotta e il colore li abbiamo già in availableStates, scaricati con ?lang=
        const known = get().availableStates.find(s => s.id === machineStateId);
        set({
          currentStateLog: {
            ...created,
            machineState: {
              ...created.machineState,
              valueLocale: known?.valueLocale ?? created.machineState.valueIt,
              color: known?.color ?? created.machineState.color,
            },
          },
        });
      },

      async refreshProductionSummary() {
        const { sessionId } = get();
        if (!sessionId) return;
        const { data } = await pwaApi.get(`/tablet/sessions/${sessionId}/production/summary`);
        set({ productionSummary: { producedTotal: Number(data.data.producedTotal), rejectTotal: Number(data.data.rejectTotal) } });
      },

      setOnline(online) {
        set({ isOnline: online });
      },
    }),
    {
      name: 'biofarma-pwa-session',
      storage: createJSONStorage(() => sessionStorage),
      // `language` is deliberately left out: it is owned by localStorage (LANG_STORAGE_KEY) so it
      // survives app restarts, and a stale sessionStorage copy must never win over it.
      partialize: ({ language: _language, ...rest }) => rest,
    }
  )
);
